import { useMemo } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Landmark,
  Banknote,
  Wallet,
  FolderOpen,
  Pencil,
  Power,
  PowerOff,
  ArrowUpRight,
  ArrowDownLeft,
  Copy,
  Check,
} from 'lucide-react'
import { useCuentaEstadisticas } from '@/hooks/useCuentas'
import { toast } from 'sonner'
import { useState } from 'react'
import type { CuentaConSaldo } from '@/services/cuentasService'
import { formatDateOrDash as formatDate } from '@/lib/utils'

interface CuentaDetalleDialogProps {
  cuenta: CuentaConSaldo | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onEditar: (cuenta: CuentaConSaldo) => void
  onToggleActiva: (cuenta: CuentaConSaldo) => void
}

// Formato de moneda
const formatCurrency = (value: number, moneda: string = 'ARS') =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: moneda }).format(value)

// Icono según tipo
function getTipoIcon(tipo: string | null) {
  const iconClass = 'h-5 w-5'
  switch (tipo) {
    case 'Banco':
      return <Landmark className={iconClass} />
    case 'Efectivo':
      return <Banknote className={iconClass} />
    case 'Wallet':
    case 'MercadoPago':
      return <Wallet className={iconClass} />
    default:
      return <FolderOpen className={iconClass} />
  }
}

// Componente para copiar texto
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      toast.success('Copiado al portapapeles')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Error al copiar')
    }
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-6 w-6"
      onClick={handleCopy}
      aria-label={copied ? 'Copiado' : 'Copiar'}
      title={copied ? 'Copiado' : 'Copiar'}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  )
}

// Fila de detalle
function DetailRow({
  label,
  value,
  copyable = false,
}: {
  label: string
  value: string | React.ReactNode
  copyable?: boolean
}) {
  return (
    <div className="flex justify-between items-start py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1">
        <span className="text-sm font-medium text-right">{value}</span>
        {copyable && typeof value === 'string' && <CopyButton text={value} />}
      </div>
    </div>
  )
}

export function CuentaDetalleDialog({
  cuenta,
  open,
  onOpenChange,
  onEditar,
  onToggleActiva,
}: CuentaDetalleDialogProps) {
  // Fetch estadísticas si hay cuenta
  const { data: estadisticas, isLoading: loadingStats } = useCuentaEstadisticas(
    cuenta?.id || ''
  )

  // Información calculada
  const info = useMemo(() => {
    if (!cuenta) return null

    const saldoActual = cuenta.saldo_actual || 0
    const moneda = cuenta.moneda || 'ARS'

    return {
      saldoFormateado: formatCurrency(saldoActual, moneda),
      saldoPositivo: saldoActual >= 0,
      fechaCreacion: formatDate(cuenta.created_at),
      ultimoMovimiento: formatDate(cuenta.ultimo_movimiento),
      totalMovimientos: cuenta.total_movimientos || 0,
    }
  }, [cuenta])

  if (!cuenta || !info) return null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-3">
            <div
              className={`p-2 rounded-lg ${
                cuenta.tipo === 'Banco'
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-400'
                  : cuenta.tipo === 'Efectivo'
                    ? 'bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400'
                    : cuenta.tipo === 'Wallet'
                      ? 'bg-violet-100 text-violet-600 dark:bg-violet-950 dark:text-violet-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }`}
            >
              {getTipoIcon(cuenta.tipo)}
            </div>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-lg truncate">{cuenta.nombre}</SheetTitle>
              <SheetDescription asChild>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Badge
                    variant={cuenta.activa ? 'default' : 'secondary'}
                    className={cuenta.activa ? 'bg-green-600' : ''}
                  >
                    {cuenta.activa ? 'Activa' : 'Inactiva'}
                  </Badge>
                  <span>{cuenta.tipo}</span>
                </div>
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Contenido scrolleable */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {/* Saldo actual - destacado */}
          <div className="rounded-lg border bg-card p-4 mb-6">
            <p className="text-sm text-muted-foreground mb-1">Saldo Actual</p>
            <p
              className={`text-3xl font-bold tabular-nums ${
                info.saldoPositivo ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {info.saldoFormateado}
            </p>
            {cuenta.saldo_inicial !== null && cuenta.saldo_inicial !== 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Saldo inicial: {formatCurrency(cuenta.saldo_inicial || 0, cuenta.moneda || 'ARS')}
              </p>
            )}
          </div>

          {/* Estadísticas de movimientos */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground mb-1">Movimientos</p>
              {loadingStats ? (
                <Skeleton className="h-6 w-12" />
              ) : (
                <p className="text-xl font-semibold tabular-nums">
                  {estadisticas?.totalMovimientos ?? info.totalMovimientos}
                </p>
              )}
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground mb-1">Último Mov.</p>
              <p className="text-sm font-medium">{info.ultimoMovimiento}</p>
            </div>
          </div>

          {/* Flujo de fondos */}
          {loadingStats ? (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
          ) : estadisticas && (estadisticas.totalDebe > 0 || estadisticas.totalHaber > 0) ? (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-900 p-3">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <ArrowDownLeft className="h-4 w-4" />
                  <span className="text-xs">Ingresos</span>
                </div>
                <p className="text-lg font-semibold text-green-600 tabular-nums">
                  {formatCurrency(estadisticas.totalHaber, cuenta.moneda || 'ARS')}
                </p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 p-3">
                <div className="flex items-center gap-2 text-red-600 mb-1">
                  <ArrowUpRight className="h-4 w-4" />
                  <span className="text-xs">Egresos</span>
                </div>
                <p className="text-lg font-semibold text-red-600 tabular-nums">
                  {formatCurrency(estadisticas.totalDebe, cuenta.moneda || 'ARS')}
                </p>
              </div>
            </div>
          ) : null}

          <Separator className="my-4" />

          {/* Detalles de la cuenta */}
          <div className="space-y-1">
            <h4 className="text-sm font-medium mb-3">Información de la Cuenta</h4>

            <DetailRow label="Tipo" value={cuenta.tipo || '-'} />
            <DetailRow label="Institución" value={cuenta.institucion || '-'} />
            {cuenta.cbu_cvu_alias && (
              <DetailRow
                label="CBU/CVU/Alias"
                value={cuenta.cbu_cvu_alias}
                copyable
              />
            )}
            <DetailRow label="Moneda" value={cuenta.moneda || 'ARS'} />
            <DetailRow label="Creada" value={info.fechaCreacion} />

            {cuenta.observaciones && (
              <>
                <Separator className="my-3" />
                <div className="py-2">
                  <p className="text-sm text-muted-foreground mb-1">Observaciones</p>
                  <p className="text-sm">{cuenta.observaciones}</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer con acciones */}
        <div className="px-6 py-4 border-t bg-background mt-auto">
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                onOpenChange(false)
                onEditar(cuenta)
              }}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Editar
            </Button>
            <Button
              variant={cuenta.activa ? 'outline' : 'default'}
              className="flex-1"
              onClick={() => {
                onToggleActiva(cuenta)
                onOpenChange(false)
              }}
            >
              {cuenta.activa ? (
                <>
                  <PowerOff className="h-4 w-4 mr-2" />
                  Desactivar
                </>
              ) : (
                <>
                  <Power className="h-4 w-4 mr-2" />
                  Activar
                </>
              )}
            </Button>
          </div>
          {/* TODO: Agregar botón para ver estado de cuenta completo cuando exista esa página */}
          {/* <Button variant="ghost" className="w-full mt-2">
            <ExternalLink className="h-4 w-4 mr-2" />
            Ver Estado de Cuenta Completo
          </Button> */}
        </div>
      </SheetContent>
    </Sheet>
  )
}
